import { RequestProvider, TrackingProvider, LightboxContextProvider } from "mibaby-frontend/contexts";
import { createStore, store } from "@store";
import { fetchUserState } from "@store/profile";
import { showRegisterLightbox } from "@components/with-anonymous";
import { initQueryClient, queryClient } from "mibaby-frontend/queries";
import { initDate } from "mibaby-frontend/util/date";
import { qsa, ready } from "@util/dom";
import { load as loadHypernova } from "hypernova";
import { renderReact } from "hypernova-react";
import { LocaleProvider } from "mibaby-frontend/contexts";
import onmount from "onmount";
import { render } from "react-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "react-redux";
import { HashRouter } from "react-router-dom";
import { loadComponent } from "./loadComponent";
import type { V2SsrMetadata } from "mibaby-common/types";

/**
 * Client bootstrap loaded by `index.js`: builds the shared provider tree (Redux store,
 * React Query, request/tracking/locale/lightbox contexts) and mounts individual
 * components into the page in two ways:
 *  - `.react-me[data-component]` elements are plain client-side mounts — `loadComponent`
 *    resolves the name from `data-component` and `render()`s it with `data-props` as
 *    props (used by CakePHP views that don't need SSR, e.g. checklists, dynamic forms).
 *  - `div[data-hypernova-key]` elements were already server-rendered by Hypernova
 *    (`ssr.jsx`, via `SsrJsHelper::render()`); this re-mounts the same component
 *    client-side for interactivity and, on the initial `ready()` pass, harvests each
 *    one's `initialData` (via `hypernova`'s `load()`) into the Redux store before
 *    dispatching `fetchUserState`.
 *
 * Part of the V2 React App component — see docs/wiki/System/v2/frontend/v2-react-app.md.
 */

const metadata: V2SsrMetadata = JSON.parse(window["SSR_METADATA"]?.text ?? "{}");

initQueryClient(metadata);
initDate(metadata);

const AppContext = ({ children }) => (
    <RequestProvider metadata={metadata}>
        <TrackingProvider metadata={metadata}>
            <LocaleProvider>
                <QueryClientProvider client={queryClient}>
                    <LightboxContextProvider showLightbox={() => {}} showRegisterLightbox={showRegisterLightbox}>
                        <Provider store={store}>{children}</Provider>
                    </LightboxContextProvider>
                    <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
            </LocaleProvider>
        </TrackingProvider>
    </RequestProvider>
);

// Collect store data from server-rendered components
ready(() => {
    createStore(
        qsa("[data-hypernova-key]").reduce((aggr, element) => {
            const matches = loadHypernova(element.dataset.hypernovaKey);
            const initialData = matches[0]?.data?.initialData;

            return matches.length === 1 && initialData ? { ...aggr, ...initialData } : aggr;
        }, {})
    );

    store.dispatch(fetchUserState(metadata));
});

// Load client-only components
onmount(".react-me", function (this: HTMLElement) {
    const { component } = this.dataset;
    if (!component) return;

    loadComponent(component).then(Component => {
        const props = JSON.parse(this.dataset.props ?? "{}");

        render(
            <AppContext>
                <Component {...this.dataset} {...props} key={this.id} id={this.id} />
            </AppContext>,
            this
        );
    });
});

// Load server-rendered components
onmount("div[data-hypernova-key]", function (this: HTMLElement) {
    const { hypernovaKey: key, hypernovaId: id } = this.dataset;
    if (!key) return;

    loadComponent(key).then(Component => {
        renderReact(key, (props: object) => (
            <HashRouter>
                <AppContext>
                    <Component {...props} key={id} id={id} />
                </AppContext>
            </HashRouter>
        ));
    });
});

/*
 * Types
 */

declare global {
    interface Window {
        SSR_METADATA: {
            text: string;
        };
    }
}
